import { Link, useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { useState } from 'react'
import type { ApiError } from '@/lib/api'
import {
  buildInstitutionPasswordResetUrl,
  institutionForgotPassword,
} from '@/lib/institution-password'
import loginStyles from '@/pages/LoginPage/LoginPage.module.css'

function extractErrorMessage(error: unknown): string {
  const fallback = 'Não foi possível enviar o link. Tente novamente.'
  if (!error || typeof error !== 'object') return fallback
  const apiError = error as ApiError
  const details = apiError.details as
    | { message?: string; errors?: Record<string, string[]> }
    | undefined

  if (details?.errors) {
    const firstKey = Object.keys(details.errors)[0]
    const firstMessage = firstKey ? details.errors[firstKey]?.[0] : undefined
    if (firstMessage) return firstMessage
  }

  if (details?.message) return details.message
  return apiError.message || fallback
}

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrorMessage(null)
    setSuccessMessage(null)
    setIsSubmitting(true)

    try {
      const response = await institutionForgotPassword({
        email: email.trim().toLowerCase(),
        reset_url: buildInstitutionPasswordResetUrl(),
      })
      setSuccessMessage(
        response.message ??
          'Se o e-mail existir, enviaremos um link para redefinir a senha por e-mail e WhatsApp.'
      )
      window.setTimeout(() => navigate('/login', { replace: true }), 2500)
    } catch (error) {
      setErrorMessage(extractErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={loginStyles.page}>
      <div className={loginStyles.card}>
        <h1 className={loginStyles.title}>Recuperar senha</h1>
        <p className={loginStyles.subtitle}>
          Informe seu e-mail para receber o link por e-mail e WhatsApp
        </p>

        <form className={loginStyles.form} onSubmit={handleSubmit}>
          <div className={loginStyles.field}>
            <label className={loginStyles.label} htmlFor="forgot-email">
              E-mail
            </label>
            <input
              id="forgot-email"
              type="email"
              className={loginStyles.input}
              placeholder="seu@email.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={Boolean(successMessage)}
            />
          </div>

          {errorMessage && (
            <div className={loginStyles.field} role="alert">
              <span className={loginStyles.subtitle}>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className={loginStyles.field} role="status">
              <span className={loginStyles.subtitle}>{successMessage}</span>
            </div>
          )}

          <button
            type="submit"
            className={loginStyles.submit}
            disabled={isSubmitting || Boolean(successMessage)}
          >
            <Icon icon="mdi:email-send-outline" className={loginStyles.submitIcon} />
            {isSubmitting ? 'Enviando...' : 'Enviar link'}
          </button>
        </form>

        <div className={loginStyles.footer}>
          <p className={loginStyles.footerText}>
            Lembrou a senha?
            <Link to="/login" className={loginStyles.signupLink}>
              Voltar ao login
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
